export const RAW_STORE = "raw";
export const WRITE_STORE = "write";

export interface Store {
    put: (key: number, data: Uint8Array, store: string) => Promise<void>;
    get: (key: number, store: string) => Promise<Uint8Array | null>;
    keys: (store: string) => Promise<number[]>;
    each: (key: number[], store: string, callback: (key: number, data: Uint8Array) => void) => Promise<void>;
    close: () => void;
}

export class NoStore implements Store {
    public owner = "";

    public close() {
    }

    public put(key: number, data: Uint8Array, store: string): Promise<void> {
        return Promise.resolve();
    }

    public get(range: number, store: string): Promise<Uint8Array | null> {
        return Promise.resolve(null);
    }

    public keys(store: string): Promise<number[]> {
        return Promise.resolve([]);
    }

    public each(keys: number[], store: string, callback: (key: number, data: Uint8Array) => void) {
        return Promise.resolve();
    }
}

class DbStore implements Store {
    private indexedDB: IDBFactory;
    private db: IDBDatabase | null = null;

    constructor(
        url: string,
        onready: (cache: Store) => void,
        onerror: (msg: string) => void) {
        this.indexedDB = (typeof window === "undefined" ? undefined : window.indexedDB ||
            (window as any).mozIndexedDB ||
            (window as any).webkitIndexedDB || (window as any).msIndexedDB) as any;

        if (!this.indexedDB) {
            onerror("IndexedDB is not supported on this host");
            return;
        }

        this.open("sockdrive (" + url + ")", onready, onerror, true);
    }

    private open(
        name: string,
        onready: (cache: Store) => void,
        onerror: (msg: string) => void,
        allowHeal: boolean) {
        try {
            const openRequest = this.indexedDB.open(name, 1);
            openRequest.onerror = () => {
                onerror("Can't open cache database: " + openRequest.error?.message);
            };
            openRequest.onsuccess = () => {
                const db = openRequest.result;
                // Self-heal a poisoned database: it can exist at version 1
                // WITHOUT its object stores (an earlier creation aborted
                // mid-upgrade). Every transaction on it would throw
                // NotFoundError — delete and recreate once.
                if (!db.objectStoreNames.contains(RAW_STORE) ||
                        !db.objectStoreNames.contains(WRITE_STORE)) {
                    db.close();
                    if (!allowHeal) {
                        onerror("Cache database has no object stores");
                        return;
                    }
                    console.warn("sockdrive cache", name, "missing object stores — recreating");
                    const del = this.indexedDB.deleteDatabase(name);
                    const retry = () => this.open(name, onready, onerror, false);
                    del.onsuccess = retry;
                    del.onerror = retry;
                    del.onblocked = () => onerror("Cache database is broken and held open elsewhere");
                    return;
                }
                // A delete or upgrade of this cache from another tab (an
                // uninstall, "Clear streamed data") waits until every open
                // connection closes, and every later open of the cache, in
                // any tab, waits behind it. A running game never closed its
                // own, so the next boot sat on "Preparing game files" for as
                // long as the old tab lived. Let go instead: this session
                // reads on without the cache (see the null checks below).
                db.onversionchange = () => {
                    db.close();
                    if (this.db === db) {
                        this.db = null;
                    }
                };
                this.db = db;
                onready(this);
            };
            openRequest.onupgradeneeded = () => {
                try {
                    const db = openRequest.result;
                    db.onerror = () => {
                        onerror("Can't upgrade cache database");
                    };

                    db.createObjectStore(RAW_STORE)
                        .createIndex("range", "", { multiEntry: false });
                    db.createObjectStore(WRITE_STORE)
                        .createIndex("sector", "", { multiEntry: false });
                } catch (e) {
                    // Abort so the half-made schema is rolled back instead of
                    // committing a store-less database (the poison above).
                    try {
                        openRequest.transaction?.abort();
                    } catch (e2) {
                        // ignore
                    }
                    onerror("Can't upgrade cache database");
                }
            };
        } catch (e: any) {
            onerror("Can't open cache database: " + e.message);
        }
    }

    public close() {
        if (this.db !== null) {
            this.db.close();
            this.db = null;
        }
    }

    public put(key: number, data: Uint8Array, store: string): Promise<void> {
        return new Promise<void>((resolve) => {
            const transaction = this.transaction(store, "readwrite");
            if (transaction === null) {
                resolve();
                return;
            }
            const request = transaction.objectStore(store).put(new Blob([data.buffer]), key);
            request.onerror = (e) => {
                console.error(e);
                resolve();
            };
            request.onsuccess = () => {
                resolve();
            };
        });
    }

    public get(key: number, store: string): Promise<Uint8Array | null> {
        return new Promise<Uint8Array | null>((resolve) => {
            const transaction = this.transaction(store, "readonly");
            if (transaction === null) {
                resolve(null);
                return;
            }
            const request = transaction.objectStore(store).get(key) as IDBRequest<ArrayBuffer | Blob>;
            request.onerror = (e) => {
                console.error(e);
                resolve(null);
            };
            request.onsuccess = () => {
                if (request.result) {
                    (request.result as Blob).arrayBuffer().then((buffer) => {
                        resolve(new Uint8Array(buffer));
                    }).catch((e) => {
                        console.error(e);
                        resolve(null);
                    });
                } else {
                    resolve(null);
                }
            };
        });
    }

    public keys(store: string): Promise<number[]> {
        return new Promise<number[]>((resolve) => {
            const transaction = this.transaction(store, "readonly");
            if (transaction === null) {
                resolve([]);
                return;
            }
            const request = transaction.objectStore(store).getAllKeys();
            request.onerror = (e) => {
                console.error(e);
                resolve([]);
            };
            request.onsuccess = (event) => {
                if (request.result) {
                    resolve(request.result as number[]);
                } else {
                    resolve([]);
                }
            };
        });
    }

    public each(keys: number[], storeName: string, callback: (key: number, data: Uint8Array) => void) {
        return new Promise<void>((resolve) => {
            const transaction = this.transaction(storeName, "readonly");
            if (transaction === null) {
                resolve();
                return;
            }
            const store = transaction.objectStore(storeName);

            const readOne = async (key: number) => {
                return new Promise<Uint8Array>((resolve, reject) => {
                    const request = store.get(key);
                    request.onerror = (e) => {
                        reject(e);
                    };
                    request.onsuccess = (event) => {
                        (request.result as Blob).arrayBuffer()
                            .then((buffer) => {
                                resolve(new Uint8Array(buffer));
                            }).catch(reject);
                    };
                });
            };

            (async () => {
                for (const key of keys) {
                    const data = await readOne(key);
                    callback(key, data);
                }
                resolve();
            })().catch((e) => {
                console.error(e);
                resolve();
            });
        });
    }

    /** A transaction on the cache, or null once it was let go (or is closing). */
    private transaction(store: string, mode: IDBTransactionMode): IDBTransaction | null {
        if (this.db === null) {
            return null;
        }
        try {
            return this.db.transaction(store, mode);
        } catch (e) {
            return null;
        }
    }
}

/**
 * How long the cache may take to open before the drive goes on without it.
 * A healthy open takes milliseconds. One that takes longer is queued behind a
 * delete or upgrade that another tab is still holding, and nothing here can
 * end that wait.
 */
const OPEN_DEADLINE_MS = 5000;

export function getStore(owner: string): Promise<Store> {
    return new Promise((resolve) => {
        let settled = false;
        const timer = setTimeout(() => {
            settled = true;
            console.warn("sockdrive cache", owner, "did not open in", OPEN_DEADLINE_MS,
                "ms (another tab is holding it) — streaming without it");
            resolve(new NoStore());
        }, OPEN_DEADLINE_MS);
        new DbStore(owner, (store) => {
            if (settled) {
                // Opened after the drive went on without it: holding it
                // would block the next delete in turn.
                store.close();
                return;
            }
            settled = true;
            clearTimeout(timer);
            resolve(store);
        }, (msg: string) => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            console.error("Can't open IndexedDB cache", msg);
            resolve(new NoStore());
        });
    });
}
